import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { FluentProvider } from '@fluentui/react-components'
import App from './App'
import './app.css'
import { AuthProvider } from './auth'
import { bootOrAnswer } from './embedded'
import { DensityProvider } from './platform'
import { ThemeChoiceContext } from './themeContext'
import { readThemeId, rememberThemeId, themeFor } from './theme'

/**
 * One FluentProvider at the root, one DensityProvider inside it.
 *
 * Everything visual below this point reads tokens from the resolved theme, so
 * changing a theme is a context swap and never a reload, and no page holds a
 * colour of its own.
 */
function Root() {
  const [themeId, setThemeId] = useState(readThemeId)

  const choose = (id: string) => {
    rememberThemeId(id)
    setThemeId(id)
  }

  return (
    <FluentProvider theme={themeFor(themeId)} style={{ height: '100%' }}>
      <DensityProvider defaultDensity="cozy">
        <ThemeChoiceContext.Provider value={{ themeId, chooseTheme: choose }}>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </ThemeChoiceContext.Provider>
      </DensityProvider>
    </FluentProvider>
  )
}

const mount = document.getElementById('root')!

// Before anything else, and before React exists.
//
// The sign-in library renews an expired token through a hidden iframe on the
// redirect URI, and this service answers that path with index.html, so the
// console used to boot a second time inside it. Nothing below this line may
// run there: not the router, not the auth provider, not a single fetch. The
// guard is here rather than inside AuthProvider because by the time a provider
// could refuse, the second boot has already happened.
void bootOrAnswer({
  mount,
  boot: () =>
    createRoot(mount).render(
      <StrictMode>
        <Root />
      </StrictMode>,
    ),
})
